import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import OrderCard from '../components/OrderCard';
import { connectAdminSocket } from '../socket';

const FILTER_TABS = [
  { key: 'all', label: '전체' },
  { key: 'accepted', label: '접수 완료', color: 'var(--primary)' },
  { key: 'cancelled', label: '취소됨', color: 'var(--text-tertiary)' },
];

let toastCounter = 0;
const useToast = () => {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((msg, type = 'info') => {
    const id = ++toastCounter;
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3500);
  }, []);
  return { toasts, addToast };
};

const IconCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconXMark = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const IconBell = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);
const IconRefresh = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <polyline points="23 4 23 10 17 10"/>
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
  </svg>
);

const Toast = ({ toasts }) => (
  <div className="toast-container">
    {toasts.map((t) => (
      <div key={t.id} className={`toast ${t.type}`}>
        <span className="toast-icon">{t.type === 'success' ? <IconCheck /> : t.type === 'error' ? <IconXMark /> : <IconBell />}</span>
        <div className="toast-content"><div className="toast-message">{t.msg}</div></div>
      </div>
    ))}
  </div>
);

const playBeep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [880, 1100].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      const t = ctx.currentTime + i * 0.18;
      gain.gain.setValueAtTime(0.25, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
      osc.start(t);
      osc.stop(t + 0.35);
    });
  } catch {}
};

const printOrderTicket = (order) => {
  const items = order.items || order.orderItems || [];
  const tableNum = order.tableNumber || order.table_number || '?';
  const totalAmount = order.totalAmount || order.total_amount || 0;
  const orderId = order._id || order.id || '';
  const createdAt = order.createdAt || order.created_at || '';
  const timeStr = createdAt ? new Date(createdAt).toLocaleString('ko-KR') : '';

  const itemsHtml = items.map((item) => {
    const name = item.menuName || item.name || item.menu_name || '';
    const qty = item.quantity || item.qty || 1;
    const price = item.price || item.unitPrice || item.unit_price || 0;
    let opts = item.options || [];
    if (typeof opts === 'string') { try { opts = JSON.parse(opts); } catch { opts = []; } }
    const optsHtml = opts.length > 0
      ? `<div style="font-size:11px;color:#888;padding-left:8px;">${opts.map((o) => `+ ${o.name}`).join(', ')}</div>`
      : '';
    return `<div style="display:flex;justify-content:space-between;margin-bottom:4px;"><span>${name} x${qty}</span><span>${Number(price * qty).toLocaleString()}원</span></div>${optsHtml}`;
  }).join('');

  const html = `<!DOCTYPE html><html><head><title>주문표</title><style>
    body{font-family:'Noto Sans KR',sans-serif;padding:20px;max-width:300px;margin:0 auto;font-size:13px;}
    h2{text-align:center;margin-bottom:8px;font-size:16px;}
    .divider{border-top:1px dashed #999;margin:10px 0;}
    .total{font-size:16px;font-weight:700;display:flex;justify-content:space-between;margin-top:8px;}
    @media print{body{padding:0;}}
  </style></head><body>
    <h2>주문표</h2>
    <div class="divider"></div>
    <div><strong>주문번호:</strong> ${orderId.slice(-8)}</div>
    <div><strong>테이블:</strong> ${tableNum}번</div>
    <div><strong>시간:</strong> ${timeStr}</div>
    <div class="divider"></div>
    ${itemsHtml}
    <div class="divider"></div>
    <div class="total"><span>합계</span><span>${Number(totalAmount).toLocaleString()}원</span></div>
  </body></html>`;

  const win = window.open('', '_blank', 'width=350,height=500');
  if (win) {
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.print(); }, 300);
  }
};

const OrdersPage = () => {
  const { user } = useAuth();
  const { toasts, addToast } = useToast();
  const storeId = user?.storeId || user?.store_id;

  const [orders, setOrders] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const socketRef = useRef(null);
  const refreshRef = useRef(null);
  const prevOrderCountRef = useRef(0);

  const fetchOrders = useCallback(async () => {
    if (!storeId) {
      setOrders([]);
      setIsLoading(false);
      return;
    }
    try {
      const res = await api.get(`/stores/${storeId}/orders?limit=100&sort=-createdAt`);
      const data = res.data;
      setOrders(Array.isArray(data) ? data : data.orders || []);
    } catch {
      setOrders([]);
    } finally {
      setIsLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchOrders();

    // Socket
    if (storeId) {
      const socket = connectAdminSocket(storeId);
      socketRef.current = socket;

      socket.on('new_order', (data) => {
        if (soundEnabled) playBeep();
        addToast(`새 주문이 들어왔습니다! 테이블 ${data.tableNumber || data.table_number}번`, 'info');
        setOrders((prev) => {
          const exists = prev.some((o) => (o._id || o.id) === (data._id || data.id));
          if (exists) return prev;
          return [data, ...prev];
        });
      });

      socket.on('order_status_change', (data) => {
        setOrders((prev) =>
          prev.map((o) =>
            (o._id || o.id) === (data.orderId || data._id || data.id)
              ? { ...o, status: data.status }
              : o
          )
        );
      });
    }

    refreshRef.current = setInterval(fetchOrders, 30000);

    return () => {
      if (socketRef.current) {
        socketRef.current.off('new_order');
        socketRef.current.off('order_status_change');
      }
      clearInterval(refreshRef.current);
    };
  }, [fetchOrders, storeId, addToast, soundEnabled]);

  // Sound alert when order count increases via polling
  useEffect(() => {
    const currentCount = orders.length;
    if (currentCount > prevOrderCountRef.current && prevOrderCountRef.current > 0 && soundEnabled) {
      playBeep();
    }
    prevOrderCountRef.current = currentCount;
  }, [orders.length, soundEnabled]);

  const handleStatusChange = useCallback(async (orderId, newStatus) => {
    try {
      if (storeId) {
        await api.put(`/orders/${orderId}/status`, { status: newStatus });
      }
      setOrders((prev) =>
        prev.map((o) => (o._id || o.id) === orderId ? { ...o, status: newStatus } : o)
      );
      addToast(newStatus === 'cancelled' ? '주문이 취소되었습니다.' : '주문이 처리되었습니다.', 'success');
    } catch {
      addToast('상태 변경에 실패했습니다.', 'error');
    }
  }, [storeId, addToast]);

  const filteredOrders = activeFilter === 'all'
    ? orders
    : orders.filter((o) => o.status === activeFilter);

  const countByStatus = (status) => orders.filter((o) => o.status === status).length;

  if (isLoading) {
    return (
      <div className="page-container">
        <div style={{ marginBottom: 28 }}>
          <div className="skeleton-line h-24 w-60" />
          <div className="skeleton-line w-40" style={{ height: 12, marginTop: 8 }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {[0,1,2,3,4,5].map((i) => (
            <div key={i} className="skeleton-card animate-fade-in-up" style={{ animationDelay: `${i * 50}ms`, padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div className="skeleton-circle" style={{ width: 42, height: 42, borderRadius: 12 }} />
                <div style={{ flex: 1 }}>
                  <div className="skeleton-line w-60" style={{ height: 12 }} />
                  <div className="skeleton-line w-40" style={{ height: 10 }} />
                </div>
              </div>
              <div className="skeleton-line w-80" />
              <div className="skeleton-line w-60" />
              <div className="skeleton-line w-40" style={{ marginTop: 12 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <Toast toasts={toasts} />

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">주문 관리</h1>
          <p className="page-subtitle">실시간 주문 현황 · 총 {orders.length}건</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {countByStatus('pending') > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--danger-light)', padding: '8px 14px', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(240,68,82,0.2)' }}>
              <span style={{ animation: 'pulse-dot 1.5s infinite', background: 'var(--danger)', width: 8, height: 8, borderRadius: '50%', display: 'inline-block' }} />
              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--danger)' }}>
                대기 {countByStatus('pending')}건
              </span>
            </div>
          )}
          {countByStatus('accepted') > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(191,155,48,0.08)', padding: '8px 14px', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(191,155,48,0.2)' }}>
              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--primary)' }}>
                접수 {countByStatus('accepted')}건
              </span>
            </div>
          )}
          <button
            className={`btn btn-sm ${soundEnabled ? 'btn-outline' : 'btn-ghost'}`}
            onClick={() => setSoundEnabled(!soundEnabled)}
            title={soundEnabled ? '알림음 끄기' : '알림음 켜기'}
            style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '1rem' }}
          >
            {soundEnabled ? '\uD83D\uDD14' : '\uD83D\uDD15'}
          </button>
          <button className="btn btn-outline btn-sm" onClick={fetchOrders} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <IconRefresh /> 새로고침
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="tab-bar">
        {FILTER_TABS.map((tab) => {
          const count = tab.key === 'all' ? orders.length : countByStatus(tab.key);
          return (
            <button
              key={tab.key}
              className={`tab-btn ${activeFilter === tab.key ? 'active' : ''}`}
              onClick={() => setActiveFilter(tab.key)}
            >
              {tab.label}
              {count > 0 && (
                <span
                  style={{
                    background: activeFilter === tab.key ? 'var(--primary)' : 'var(--border)',
                    color: activeFilter === tab.key ? '#fff' : 'var(--text-secondary)',
                    borderRadius: 10,
                    padding: '1px 7px',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                  }}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Orders board */}
      {filteredOrders.length === 0 ? (
        <div className="card" style={{ marginTop: 8 }}>
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
                <line x1="8" y1="18" x2="21" y2="18"/>
                <circle cx="3" cy="6" r="1" fill="currentColor"/>
                <circle cx="3" cy="12" r="1" fill="currentColor"/>
                <circle cx="3" cy="18" r="1" fill="currentColor"/>
              </svg>
            </div>
            <h3>
              {activeFilter === 'all' ? '주문이 없습니다' : `${FILTER_TABS.find((t) => t.key === activeFilter)?.label} 주문이 없습니다`}
            </h3>
            <p>새 주문이 들어오면 실시간으로 알림이 표시됩니다.</p>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {filteredOrders
            .sort((a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt))
            .map((order, idx) => (
              <div key={order._id || order.id} className="animate-fade-in-up" style={{ animationDelay: `${idx * 50}ms` }}>
                <OrderCard
                  order={order}
                  onStatusChange={handleStatusChange}
                />
                <button
                  className="btn btn-outline btn-sm w-full"
                  style={{ marginTop: -4, borderTopLeftRadius: 0, borderTopRightRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                  onClick={() => printOrderTicket(order)}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <polyline points="6 9 6 2 18 2 18 9"/>
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                    <rect x="6" y="14" width="12" height="8"/>
                  </svg>
                  인쇄
                </button>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
